import type { VenueReservation } from "@/graphql/operations";
import { Box } from "@mui/material";
import {
  endOfMonth,
  format,
  isSameMonth,
  isToday,
  startOfMonth,
} from "date-fns";
import type { RefObject } from "react";
import ReservationCalendarDayCell from "./ReservationCalendarDayCell";
import ReservationCalendarDaySkeleton from "./ReservationCalendarDaySkeleton";

type ReservationCalendarDayGridProps = {
  calendarDays: Date[];
  calendarLoadingRange: { from: Date; until: Date } | null;
  loading: boolean;
  monthStart: Date;
  onDateClick?: (date: string) => void;
  reservationsByDate: Map<string, VenueReservation[]>;
  maxVisibleReservations: number;
  canSelectReservation: boolean;
  selectReservation?: (reservationId: string) => void;
  gridRef: RefObject<HTMLDivElement>;
};

export default function ReservationCalendarDayGrid({
  calendarDays,
  calendarLoadingRange,
  loading,
  monthStart,
  onDateClick,
  reservationsByDate,
  maxVisibleReservations,
  canSelectReservation,
  selectReservation,
  gridRef,
}: ReservationCalendarDayGridProps) {
  const loadingRangeStart = calendarLoadingRange
    ? startOfMonth(calendarLoadingRange.from).getTime()
    : null;
  const loadingRangeEnd = calendarLoadingRange
    ? endOfMonth(calendarLoadingRange.until).getTime()
    : null;

  return (
    <Box
      ref={gridRef}
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
        gridAutoRows: "1fr",
        height: "100%",
        gap: 0,
      }}
    >
      {calendarDays.map((day, dayIndex) => {
        const dateKey = format(day, "yyyy-MM-dd");
        const dayReservations = reservationsByDate.get(dateKey) ?? [];
        const isCurrentMonth = isSameMonth(day, monthStart);
        const isDayToday = isToday(day);
        const isInLoadingRange =
          loadingRangeStart !== null &&
          loadingRangeEnd !== null &&
          day.getTime() >= loadingRangeStart &&
          day.getTime() <= loadingRangeEnd;
        if (loading && isInLoadingRange) {
          const delayMs = Math.floor(dayIndex / 7) * 60 + (dayIndex % 7) * 120;
          return (
            <ReservationCalendarDaySkeleton
              key={dateKey}
              day={day}
              delayMs={delayMs}
            />
          );
        }
        return (
          <ReservationCalendarDayCell
            key={dateKey}
            day={day}
            dateKey={dateKey}
            isCurrentMonth={isCurrentMonth}
            isDayToday={isDayToday}
            dayReservations={dayReservations}
            onDateClick={onDateClick}
            maxVisibleReservations={maxVisibleReservations}
            canSelectReservation={canSelectReservation}
            selectReservation={selectReservation}
          />
        );
      })}
    </Box>
  );
}
